import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {catchError, forkJoin, of, Subject, takeUntil} from 'rxjs';
import {MaterialModule} from '../../material.module';
import {Webinar, WebinarSeries} from '../../models/webinar.model';
import {ToastService} from '../../services/layout/toast.service';
import {WebinarsService} from '../../services/webinars/webinars.service';
import {DecoratedWebinar, StatusFilter, WebinarStatus} from './webinars-admin.types';
import {WebinarsListComponent} from './webinars-list/webinars-list.component';
import {compareWebinarScheduleDate} from './webinar-schedule-sort';

@Component({
    selector: 'app-webinars-admin',
    standalone: true,
    imports: [MaterialModule, WebinarsListComponent],
    templateUrl: './webinars-admin.component.html',
})
export class WebinarsAdminComponent implements OnInit, OnDestroy {
    private readonly webinarsService = inject(WebinarsService);
    private readonly toastService = inject(ToastService);
    private readonly router = inject(Router);

    private allWebinars: DecoratedWebinar[] = [];
    webinars: DecoratedWebinar[] = [];
    series: WebinarSeries[] = [];
    seriesLoadErrorMessage: string | null = null;
    webinarsLoadErrorMessage: string | null = null;

    isLoading = false;
    filterTerm = '';
    // Multi-select filter; empty array means no restriction
    statusFilter: StatusFilter[] = [];

    statusCounts: Record<WebinarStatus, number> = {
        live: 0,
        upcoming: 0,
        draft: 0,
        past: 0,
    };

    // Map of status metadata (no 'all' option)
    readonly statusMeta: Record<WebinarStatus, { label: string; icon: string }> = {
        live: {label: 'Live now', icon: 'fiber_manual_record'},
        upcoming: {label: 'Upcoming', icon: 'schedule'},
        draft: {label: 'Draft', icon: 'pending'},
        past: {label: 'Completed', icon: 'history'},
    };

    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.loadWebinars();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refresh(): void {
        this.loadWebinars(true);
    }

    onSearchTerm(value: string): void {
        this.filterTerm = value ?? '';
        this.applyFilters();
    }

    setStatusFilter(value: StatusFilter[]): void {
        // Accept empty selection so the UI can transition from 'All' to specifics.
        // Empty array is treated as "no restriction" in applyFilters (same as ['all']).
        const next: StatusFilter[] = Array.isArray(value) ? value : [];
        // Avoid redundant filtering if unchanged
        if (Array.isArray(this.statusFilter) && this.arraysEqual(this.statusFilter, next)) return;
        this.statusFilter = next;
        this.applyFilters();
    }

    createWebinar(): void {
        this.router.navigate(['/webinars', 'new']);
    }

    openWebinar(webinar: DecoratedWebinar): void {
        this.router.navigate(['/webinars', webinar.id]);
    }

    openSeries(series: WebinarSeries): void {
        if (!series.id) return;
        this.router.navigate(['/webinars', 'series', series.id]);
    }

    private loadWebinars(showToast = false): void {
        this.isLoading = true;
        this.webinarsLoadErrorMessage = null;
        this.seriesLoadErrorMessage = null;

        forkJoin({
            webinars: this.webinarsService.getWebinars(),
            series: this.webinarsService.getWebinarSeriesList({limit: 100}).pipe(
                catchError((err) => {
                    console.error('Failed to load recurring webinar series', err);
                    this.seriesLoadErrorMessage = 'Recurring webinar series could not be loaded.';
                    return of({
                        items: [],
                        page: {
                            limit: 100,
                            offset: 0,
                            returned: 0,
                            total: 0,
                            hasMore: false,
                            truncated: false,
                            nextOffset: null,
                        },
                    });
                }),
            ),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({webinars, series}) => {
                    this.allWebinars = webinars.map((item) => this.decorateWebinar(item));
                    this.series = series.items ?? [];
                    this.webinarsLoadErrorMessage = null;
                    this.applyFilters();
                    this.isLoading = false;
                    if (showToast) {
                        this.toastService.showSuccess('Webinars updated', 2000);
                    }
                },
                error: (err) => {
                    console.error('Failed to load webinars', err);
                    this.webinarsLoadErrorMessage = 'Could not load webinars. Retry to fetch them again.';
                    this.allWebinars = [];
                    this.webinars = [];
                    this.series = [];
                    this.applyFilters();
                    this.toastService.showError('Could not load webinars', 3500);
                    this.isLoading = false;
                },
            });
    }

    private applyFilters(): void {
        const search = this.filterTerm.trim().toLowerCase();
        let filtered = [...this.allWebinars];

        // Status filtering:
        // - If selected is empty => no restriction
        // - Otherwise include if in selected set
        const selected = new Set<StatusFilter>(this.statusFilter ?? []);
        if (selected.size) {
            filtered = filtered.filter((item) => selected.has(item.status));
        }

        if (search) {
            filtered = filtered.filter((item) => {
                const title = item.title.toLowerCase();
                const slug = item.slug.toLowerCase();
                return title.includes(search) || slug.includes(search);
            });
        }

        filtered.sort((a, b) => this.compareWebinars(a, b));
        this.webinars = filtered;
        this.updateStatusCounts();
    }

    private arraysEqual(a: StatusFilter[], b: StatusFilter[]): boolean {
        if (a === b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        const setA = new Set(a);
        for (const v of b) {
            if (!setA.has(v)) return false;
        }
        return true;
    }

    private updateStatusCounts(): void {
        const counts: Record<WebinarStatus, number> = {
            live: 0,
            upcoming: 0,
            draft: 0,
            past: 0,
        };

        this.allWebinars.forEach((webinar) => {
            counts[webinar.status] += 1;
        });

        this.statusCounts = counts;
    }

    private decorateWebinar(webinar: Webinar): DecoratedWebinar {
        return {
            ...webinar,
            status: this.computeStatus(webinar),
        };
    }

    private computeStatus(webinar: Webinar): WebinarStatus {
        const now = Date.now();
        const start = webinar.scheduledAt ? new Date(webinar.scheduledAt).getTime() : null;
        const end = webinar.endAt ? new Date(webinar.endAt).getTime() : null;

        if (start && end && now >= start && now <= end) {
            return 'live';
        }

        if (start && !end && now >= start) {
            return 'live';
        }

        if (start && now < start) {
            return 'upcoming';
        }

        if ((end && now > end) || (start && end && start < end && now > end)) {
            return 'past';
        }

        if (!start) {
            return 'draft';
        }

        return now > start ? 'past' : 'draft';
    }

    private compareWebinars(a: DecoratedWebinar, b: DecoratedWebinar): number {
        const order: Record<WebinarStatus, number> = {live: 0, upcoming: 1, draft: 2, past: 3};
        if (order[a.status] !== order[b.status]) {
            return order[a.status] - order[b.status];
        }

        return compareWebinarScheduleDate(a, b, 'asc');
    }
}
